import { t } from "@lingui/macro";
import { atom } from "jotai";

import { reorderPrivateRounds } from "@/features/BookingRounds/utils/reorderPrivateRounds";
import {
  CreateCustomerRoundInput,
  CreateMemberRoundInput,
  CreateGuestRoundInput,
  NewGuestRoundInput,
  CreateLessonRequestInput,
} from "@/features/BookingWizard/hooks/useCreateLessonRequestBooking/__generated__/useCreateLessonRequestBookingMutation.graphql";

import { BookingWizard } from "../../../types";
import { BookingWizardDataAtom } from "../../stores";
import { processPrivateLessonBookingRounds } from "../../utils/processPrivateLessonBookingRounds";

type CustomerRoundInput = CreateCustomerRoundInput;

type PrivateRounds = {
  memberRounds: CreateMemberRoundInput[];
  guestRounds: CreateGuestRoundInput[];
  newGuestRounds: NewGuestRoundInput[];
};

const buildCustomerRounds = (
  rounds: BookingWizard.LessonRound[] | undefined,
  rateId: string | null,
  groupSize: number | undefined
): CustomerRoundInput[] => {
  const base: CustomerRoundInput[] = (rounds ?? []).map((round, index) => ({
    index,
    playerId: round.player.id,
    rateId: round.rate?.id ?? null,
  }));

  if (groupSize && base.length < groupSize) {
    const remainingRounds = groupSize - base.length;
    const newRounds: CustomerRoundInput[] = Array.from(
      { length: remainingRounds },
      (_, i) => ({
        index: base.length + i,
        playerId: null,
        rateId,
      })
    );
    base.push(...newRounds);
  }

  return base;
};

const buildPrivateRounds = (
  rounds: BookingWizard.LessonRound[] | undefined,
  ownerClientId: string
): PrivateRounds => {
  const memberRounds =
    processPrivateLessonBookingRounds<CreateMemberRoundInput>(rounds, [
      "member",
      "memberHold",
    ]);

  const guestRounds = processPrivateLessonBookingRounds<CreateGuestRoundInput>(
    rounds,
    ["guest", "unknownGuest"]
  );

  const newGuestRounds = processPrivateLessonBookingRounds<NewGuestRoundInput>(
    rounds,
    ["newGuest"],
    (round) => {
      const safeFirst = round.player?.firstName?.trim() ?? "";
      const safeLast = round.player?.lastName?.trim() ?? "";
      const fullName = [safeFirst, safeLast].filter(Boolean).join(" ").trim();
      return {
        name: fullName.length > 0 ? fullName : t`New Guest`,
      };
    }
  );

  return reorderPrivateRounds({
    memberRounds,
    guestRounds,
    newGuestRounds,
    ownerClientId,
  });
};

export const createLessonRequestBookingInputAtom =
  atom<CreateLessonRequestInput | null>((get) => {
    const bookingWizardData = get(BookingWizardDataAtom);
    if (
      !bookingWizardData ||
      bookingWizardData.type !== "LessonBooking" ||
      !bookingWizardData.lessonTypeId ||
      !bookingWizardData.instructorId
    ) {
      return null;
    }
    const {
      startSlotId,
      rounds,
      groupSize,
      paymentCardOnFileId,
      ownerClientId,
    } = bookingWizardData;

    const durationProperties = {
      durationMins: bookingWizardData.durationMins,
      rateId: bookingWizardData.rateId ?? null,
      isPrivate: bookingWizardData.isPrivate,
    };

    if (!startSlotId) {
      return null;
    }

    const bookingInput: CreateLessonRequestInput = {
      ...(durationProperties.isPrivate
        ? buildPrivateRounds(rounds, ownerClientId)
        : {
            customerRounds: buildCustomerRounds(
              rounds,
              durationProperties.rateId,
              groupSize
            ),
          }),
      startSlotId,
      durationMins: durationProperties.durationMins,
      lessonTypeId: bookingWizardData.lessonTypeId,
      paymentCardOnFileId,
      request: {
        maxTime: bookingWizardData.maxTime || "",
        minTime: bookingWizardData.minTime || "",
        time: bookingWizardData.requestedTime || "",
      },
      notesFromMember: "",
      notifyMember: true,
      instructors: [bookingWizardData.instructorId],
      isDraft: true,
    };

    return bookingInput;
  });
